/**
 * InfoHint · the kit's "i" button for secondary information (owner rule, 16 Oct 2026): facts, data source, evaluated
 * time, versions, method notes and "how this works" text sit behind it, so the screen shows only what a person acts on.
 *
 *   <InfoHint label="Parameter details" title="Details" facts={[['BMR step', 'p.6, step 1.2'], …]} testId="…">
 *     Optional short text under the facts.
 *   </InfoHint>
 *
 * - `label`  the button's accessible name (also the popover's name); required in practice.
 * - `title`  an optional heading inside the popover.
 * - `facts`  [label, value] pairs (falsy entries and empty values are dropped); shown as a labelled list.
 * - `children` short text under the facts.
 * - `text`   true draws the button as "i Details" (icon + the label) rather than the icon alone.
 * - `placement` the popover's placement (antd), default bottom-left.
 * - `testId` the button's test id; the popover body is `${testId}-body`.
 * A click, Enter or Space opens it; Esc closes it and returns focus to the button; Tab moving on closes it. The popover
 * is a labelled read-only region (focus stays on the button), not a dialog. Colours and spacing are tokens.
 */
import React, { useId, useRef, useState } from 'react';
import { Popover } from 'antd';
import { Info } from 'lucide-react';
import { useCpvTokens } from './useCpvTokens';
import { INFO } from './tokens';

const present = (v) => v !== null && v !== undefined && v !== '' && v !== false;

export default function InfoHint({ label = 'More information', title, facts = [], children, text = false, placement = 'bottomLeft', testId }) {
  const { vars } = useCpvTokens();
  const [open, setOpen] = useState(false);
  const button = useRef(null);
  const id = useId();
  const rows = facts.filter((f) => f && present(f[1]));
  const close = () => { setOpen(false); button.current?.focus(); };
  const body = (
    <div className="cu-info-body" id={id} role="region" aria-label={label} data-testid={testId ? `${testId}-body` : undefined}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
      {title ? <div className="cu-info-title">{title}</div> : null}
      {rows.length > 0 && (
        <dl className="cu-info-facts">
          {rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
        </dl>
      )}
      {children ? <div className="cu-info-text">{children}</div> : null}
    </div>
  );
  return (
    <Popover content={body} trigger="click" open={open} onOpenChange={setOpen} placement={placement} arrow={false}
      styles={{ root: vars }} rootClassName="cu-info-pop">
      <button ref={button} type="button" className={`cu-info${text ? ' cu-info--text' : ''}`} aria-label={text ? undefined : label}
        aria-expanded={open} aria-controls={open ? id : undefined} data-testid={testId}
        onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); e.preventDefault(); close(); }
          else if (e.key === 'Tab' && open) setOpen(false); }}>
        <Info size={text ? INFO.glyphWithText : INFO.glyph} aria-hidden />
        {text ? <span>{label}</span> : null}
      </button>
    </Popover>
  );
}
